import { leadTrackingKeys, sensitiveUrlQueryKeys } from "./tracking";
import { getFastTrackerReporter, keywordAttribution } from "./fastTracker";

export type DownloadOption = {
  industry_code: string;
  industry_name: string;
  description: string;
  icon: string;
  available: boolean;
};

type ApiEnvelope<T> = {
  code: number;
  msg: string;
  data: T;
};

export class DownloadApiError extends Error {
  constructor(
    message: string,
    readonly reason: "timeout" | "http" | "business" | "network",
  ) {
    super(message);
    this.name = "DownloadApiError";
  }
}

function endpoint(path: string) {
  const base = (import.meta.env.VITE_CLAWEASE_API_BASE_URL || "/clawease-api").trim().replace(/\/+$/, "");
  return `${base}${path}`;
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  // The download resolver reads two release manifests sequentially. Each server-side
  // read can take up to 12 seconds, so the browser timeout must leave headroom for
  // the gateway and download-event recording as well.
  const timeout = window.setTimeout(() => controller.abort(), 35_000);
  try {
    try {
      const response = await fetch(endpoint(path), {
        ...init,
        signal: init?.signal || controller.signal,
        headers: { "Content-Type": "application/json", ...init?.headers },
      });
      const body = await response.json().catch(() => null) as ApiEnvelope<T> | null;
      if (!response.ok) throw new DownloadApiError(body?.msg || `HTTP ${response.status}`, "http");
      if (!body || body.code !== 0) throw new DownloadApiError(body?.msg || "Request failed", "business");
      return body.data;
    } catch (error) {
      if (error instanceof DownloadApiError) throw error;
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new DownloadApiError("Request timed out", "timeout");
      }
      throw new DownloadApiError(error instanceof Error ? error.message : "Network request failed", "network");
    }
  } finally {
    window.clearTimeout(timeout);
  }
}

function sanitizedUrl(raw: string) {
  if (!raw) return "";
  try {
    const url = new URL(raw, window.location.origin);
    sensitiveUrlQueryKeys.forEach((key) => url.searchParams.delete(key));
    return url.toString();
  } catch {
    return "";
  }
}

export async function requestDownload(industryCode: string, platform: "windows" | "macos", architecture: "x64" | "arm64", lang: "zh" | "en", params: URLSearchParams) {
  const attribution: Record<string, string> = {};
  leadTrackingKeys.forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) attribution[key] = value;
  });

  const report = getFastTrackerReporter(window);
  const metrics = {
    ...keywordAttribution(params.toString()),
    industry_code: industryCode, platform, architecture, lang,
    source: params.get("source") || "",
    entry_source: params.get("entry_source") || "",
    plan: params.get("plan") || "",
  };
  report("download_request", metrics, "download");
  try {
    const result = await apiRequest<{ download_url: string }>("/api/v1/downloads/click", {
      method: "POST",
      body: JSON.stringify({
        industry_code: industryCode,
        platform,
        architecture,
        lang,
        plan: params.get("plan") || "",
        source: params.get("source") || "",
        entry_source: params.get("entry_source") || "",
        utm_source: params.get("utm_source") || "",
        utm_medium: params.get("utm_medium") || "",
        utm_campaign: params.get("utm_campaign") || "",
        utm_term: params.get("utm_term") || "",
        utm_content: params.get("utm_content") || "",
        page_url: sanitizedUrl(window.location.href),
        referrer: sanitizedUrl(document.referrer),
        attribution,
      }),
    });
    const url = new URL(result.download_url);
    if (url.protocol !== "https:" && url.protocol !== "http:") throw new DownloadApiError("Invalid download URL", "business");
    report("download_url_resolved", metrics, "download", true);
    return result;
  } catch (error) {
    report("download_request_failed", { ...metrics, reason: error instanceof DownloadApiError ? error.reason : "invalid_response" }, "download", true);
    throw error;
  }
}
